import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { ContributionTypeService } from "services/contribution-type.service";
import { IContributionTypeResponse } from "models/church.types";

export default function ContributionTypeList() {
  const [contributionTypes, setContributionTypes] = useState<
    IContributionTypeResponse[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 8;

  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      const result = query.trim()
        ? await ContributionTypeService.search(query, page, itemsPerPage)
        : await ContributionTypeService.getAll(page, itemsPerPage);
      setContributionTypes(result.data);
      setTotalItems(result.totalCount);
    } catch (error) {
      console.error("Erreur chargement types de contribution:", error);
      toast.error("Impossible de charger les types de contribution");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    [],
  );

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Supprimer ce type de contribution ? Cela peut affecter les registres financiers rattachés.",
      )
    ) {
      setLoading(true);
      try {
        await ContributionTypeService.delete(id);
        toast.success("Type de contribution supprimé avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        toast.error(
          "Suppression impossible, ce type est concerné par un traitement",
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* TITRE PRINCIPAL */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Types de Contribution
        </h2>
        <p className="text-slate-400 text-sm italic">
          Gestion des différentes catégories de contribution et de leur caisse de destination
        </p>
      </div>

      {/* BARRE D'OUTILS */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="relative w-full md:w-96 group">
          <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
            {loading ? (
              <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
            ) : (
              <i className="fas fa-search"></i>
            )}
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher un type..."
            className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        <Link
          to="/admin/contribution-types/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-plus-circle mr-2 text-base"></i> Nouveau Type
        </Link>
      </div>

      {/* TABLE CARD */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Type</th>
                <th className="px-6 py-5">Caisse Associée</th>
                <th className="px-6 py-5 text-center">Date de création</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {contributionTypes.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-hand-holding-heart block text-3xl mb-3 opacity-20"></i>
                    Aucun type de contribution enregistré.
                  </td>
                </tr>
              ) : (
                contributionTypes.map((type) => (
                  <tr
                    key={type.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-[#D4AF37] font-bold mr-4 border border-slate-700">
                          <i className="fas fa-coins text-xs"></i>
                        </div>
                        <div className="text-sm font-bold text-white uppercase tracking-tight">
                          {type.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {type.fundName ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-[#D4AF37] border border-slate-700 shadow-sm">
                          <i className="fas fa-wallet mr-1.5 text-[10px]"></i>
                          {type.fundName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700/50 text-slate-400 border border-slate-600/40">
                          <i className="fas fa-exclamation-circle mr-1.5 text-[10px] text-amber-500/70"></i>
                          Non rattachée
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[10px] font-medium text-center uppercase">
                      <span className="text-slate-500">
                        {type.createdDate
                          ? new Date(type.createdDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-5">
                        <Link
                          to={`/admin/contribution-types/edit/${type.id}`}
                          className="text-slate-400 hover:text-[#D4AF37]"
                          title="Modifier"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(type.id)}
                          className="text-slate-400 hover:text-red-500"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span> types
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}